import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatNumber } from '../../utils/numbersFormatting';

interface MinutesDisplayProps {
  balance?: number;
  isLowBalance?: boolean;
  showBuyButton?: boolean;
  className?: string;
  variant?: 'compact' | 'full';
  isLoading?: boolean;
  creditBalance?: number;
}

export default function MinutesDisplay({
  balance = 0,
  isLowBalance = false,
  showBuyButton = true,
  className = '',
  variant = 'compact',
  isLoading = false,
  creditBalance = 0,
}: MinutesDisplayProps) {
  const { t } = useTranslation();
  const hasBalance = balance > 0;

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="text-sm font-normal font-[NapsterSans] text-white/60">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (variant === 'full') {
    return (
      <div className={`flex flex-col gap-3 ${className}`}>
        <div
          className={`text-base font-normal font-[NapsterSans] ${
            !hasBalance
              ? 'text-red-400'
              : isLowBalance
              ? 'text-red-400'
              : 'text-white/80'
          }`}>
          {!hasBalance
            ? t('credits.noCredits')
            : isLowBalance
            ? t('credits.lowBalance', { count: formatNumber(creditBalance) })
            : t('credits.available', { count: formatNumber(creditBalance) })}
        </div>
        {showBuyButton && (
          <Link
            to="/pricing"
            className="h-[44px] px-6 bg-white hover:bg-[#F2F2F2] transition-colors duration-200 text-black rounded-[16px] font-medium font-[NapsterSans] whitespace-nowrap flex items-center justify-center">
            {t('credits.buyCredits')}
          </Link>
        )}
      </div>
    );
  }

  // Compact variant (default)
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div
        className={`text-sm font-normal font-[NapsterSans] ${
          !hasBalance
            ? 'text-red-400'
            : isLowBalance
            ? 'text-red-400'
            : 'text-white/80'
        }`}>
        {!hasBalance ? (
          <>{t('credits.noCredits')}</>
        ) : isLowBalance ? (
          <>{t('credits.lowBalance', { count: formatNumber(creditBalance) })}</>
        ) : (
          <>{t('credits.availableCompact', { count: formatNumber(creditBalance) })}</>
        )}
      </div>
      {showBuyButton && (
        <Link
          to="/pricing"
          className="bg-green-500 hover:bg-green-600 transition-colors duration-200 text-white px-3 py-1 rounded-md text-xs font-medium font-[NapsterSans] whitespace-nowrap">
          {hasBalance ? t('credits.buyCredits') : t('credits.getCredits')}
        </Link>
      )}
    </div>
  );
}
